/**
 * Reports — Next Gen Reporting surface, design `1a` (Claude Design handoff,
 * 2026-08-14). Two shelves in priority order: the standard reports Capitol
 * Commons maintains, then the org's own list. Routed at /reports;
 * /reports-legacy is the revert path.
 *
 * Approved convention departures (handoff §Departures, ratified 2026-08-14):
 * counts render as label text in section headings, not StatCard tiles; the
 * standard shelf is a card grid of launchers, not a StandardTable listing.
 *
 * Standard-report Run currently launches the retained legacy renderer —
 * Phase 4 rewires these onto Cube preset definitions. Share link / Embed are
 * affordances whose scoping ships with the share-pipeline increment; the
 * dialog carries that message (handoff: "build the two buttons as the
 * affordance and put the scoping in the dialog").
 */
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  FileBarChart,
  Flame,
  ListChecks,
  Users,
  UserSquare2,
  Vote,
  Table2,
  Landmark,
  FileText,
} from 'lucide-react';
import { AgGridReact } from 'ag-grid-react';
import { reportGridLightTheme } from '../components/shared/ag-grid-theme';
import PageShell from '../components/PageShell';
import SearchField from '../components/SearchField';
import Button from '@shared/ui/Button';
import {
  StandardTable,
  StandardTableHeader,
  StandardTableHeaderCell,
  StandardTableBody,
  StandardTableRow,
  StandardTableCell,
} from '../components/StandardTable';
import EmptyState from '../components/EmptyState';
import ActionsHeaderHelp from '../components/ActionsHeaderHelp';
import SectionHeading from '../components/SectionHeading';
import { EmptyTableState } from '../components/StandardTable';
import Pagination from '../components/Pagination';
import {
  AddIcon,
  RunIcon,
  ShareIcon,
  EmbedIcon,
  LockIcon,
  PreviewIcon,
  EditIcon,
  DuplicateIcon,
  DownloadIcon,
  DeleteIcon,
  CloseIcon,
} from '../components/icons';
import { formatDate, formatDateShort, formatTime, EMPTY_VALUE } from '../utils/dateUtils';
import { NgrReportWizard } from '../components/ngr/NgrReportWizard';
import { cubeColumnDefs, presetColumnDefs } from '../components/ngr/cubeGrid';
import {
  fetchNgrReports,
  createNgrReport,
  deleteNgrReport,
  executeNgrQuery,
  NgrReportDefinition,
  fetchNgrPresets,
  executeNgrPreset,
  NgrPreset,
} from '../services/ngrReportsService';
import { useOrganizations } from '../hooks/useOrganizations';
import api from '../lib/api';

/**
 * The eight standard reports retained from legacy reporting (product decision
 * 2026-08-14 — this is the R8 carry-forward set, not the design's proposal).
 * Run launches the legacy renderer until Phase 4 rewires them onto presets.
 */
const STANDARD_REPORTS: Array<{
  key: string;
  name: string;
  dataset: string;
  cadence: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
}> = [
  { key: 'hot-bills', name: 'Hot Bills', dataset: 'Bills', cadence: 'Live', icon: Flame, description: 'The bills your workspaces flagged hot, with calendar context and latest actions.' },
  { key: 'bill-tracking', name: 'Tracked Bills', dataset: 'Bills', cadence: 'Live', icon: ListChecks, description: 'Every tracked bill with positions, notes, sponsors, and status.' },
  { key: 'legislators', name: 'Legislators', dataset: 'Legislators', cadence: 'Rebuilt nightly', icon: Users, description: 'Tracked legislators with contact information, committees, and bill counts.' },
  { key: 'candidates', name: 'Candidates', dataset: 'Candidates', cadence: 'Rebuilt nightly', icon: UserSquare2, description: 'Tracked candidates with offices sought, committees, and contact details.' },
  { key: 'ballot-initiatives', name: 'Ballot Initiatives', dataset: 'Ballot Initiatives', cadence: 'Live', icon: Vote, description: 'Ballot measures on the ballot this cycle, laid out as citizens see them.' },
  { key: 'votes', name: 'Votes', dataset: 'Votes', cadence: 'Live', icon: Vote, description: 'The legislator × bill vote matrix across your tracked bills.' },
  { key: 'scorecard', name: 'Scorecard', dataset: 'Votes · Positions', cadence: 'Live', icon: Table2, description: 'Alignment scoring of legislator votes against your workspace positions.' },
  { key: 'sos', name: 'SOS', dataset: 'Elections', cadence: 'SoS filings, weekly', icon: Landmark, description: 'Secretary of State candidate filings rollup for the organization.' },
  { key: 'briefs', name: 'Briefs', dataset: 'Bills', cadence: 'Live', icon: FileText, description: 'Client-ready bill brief cards with AI interpretations where enabled.' },
];

const PAGE_SIZE = 20;

type StubDialog = { title: string; body: string } | null;

const NgrReportsPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { proOrganizations } = useOrganizations();
  /*
    Prefer an org this user administers, as the other report pages do, and take
    it from the PRO list — `organizations[0]` was the alphabetically first
    membership of any kind, so it could name the citizen org and derive
    isOrgAdmin from a role held somewhere the user does not work.
  */
  const organization =
    proOrganizations.find((org) =>
      ['org_admin', 'system_admin'].includes(String(org.role || '').toLowerCase())
    ) || proOrganizations[0];
  const organizationId = organization?.id;
  const isOrgAdmin = ['org_admin', 'system_admin'].includes(
    String(organization?.role || '').toLowerCase()
  );

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [wizard, setWizard] = useState<{ open: boolean; editing: NgrReportDefinition | null }>({
    open: false,
    editing: null,
  });
  const [stub, setStub] = useState<StubDialog>(null);
  const [runReport, setRunReport] = useState<NgrReportDefinition | null>(null);
  const [runPreset, setRunPreset] = useState<NgrPreset | null>(null);

  /*
    Which standard reports are Cube-backed. A shelf card whose key is absent
    still opens the legacy renderer, so the six reports migrate one at a time
    behind a parity check (standard-reports PRD §5.5/§5.6).
  */
  const { data: presets } = useQuery({
    queryKey: ['ngr-presets', organizationId],
    queryFn: () => fetchNgrPresets(organizationId!),
    enabled: Boolean(organizationId),
    staleTime: 5 * 60 * 1000,
  });
  const presetByKey = useMemo(
    () => Object.fromEntries((presets || []).map((p) => [p.key, p])),
    [presets]
  );

  const enabled = Boolean(organizationId);

  const { data: reports, isLoading } = useQuery({
    queryKey: ['ngr-reports', organizationId],
    queryFn: () => fetchNgrReports(organizationId!),
    enabled,
  });

  const { data: workspaces } = useQuery({
    queryKey: ['ngr-workspace-options'],
    queryFn: async () => {
      const res = await api.get('/workspaces');
      return (res.data.data || res.data || []) as { id: string; name: string }[];
    },
    enabled: enabled && isOrgAdmin,
    staleTime: 5 * 60 * 1000,
  });

  const duplicate = useMutation({
    mutationFn: (r: NgrReportDefinition) =>
      createNgrReport(organizationId!, {
        name: `${r.name} (Copy)`,
        description: r.description || undefined,
        config: r.config,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['ngr-reports', organizationId] }),
  });

  const remove = useMutation({
    mutationFn: (r: NgrReportDefinition) => deleteNgrReport(organizationId!, r.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['ngr-reports', organizationId] }),
  });

  const exportCsv = async (r: NgrReportDefinition) => {
    const { rows } = await executeNgrQuery(organizationId!, r.config.query);
    if (rows.length === 0) return;
    const cols = Object.keys(rows[0]);
    const csv = [
      cols.join(','),
      ...rows.map((row) => cols.map((c) => JSON.stringify(row[c] ?? '')).join(',')),
    ].join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `${r.name}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const filtered = useMemo(() => {
    const list = reports || [];
    if (!search.trim()) return list;
    const q = search.trim().toLowerCase();
    return list.filter(
      (r) => r.name.toLowerCase().includes(q) || (r.description || '').toLowerCase().includes(q)
    );
  }, [reports, search]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const availableCount = (reports || []).filter((r) => r.config?.availability != null).length;

  const shareStub = () =>
    setStub({
      title: 'Share link',
      body: 'Share links for reports arrive with the sharing update — including whether links are organization-only or public, and expiry. Nothing to configure yet.',
    });
  const embedStub = () =>
    setStub({
      title: 'Embed code',
      body: 'Embeddable report snippets arrive with the sharing update. The embed code and its scoping options will live in this dialog.',
    });

  const availabilityPill = (r: NgrReportDefinition) => {
    const a = r.config?.availability;
    if (a && 'orgWide' in a && a.orgWide) {
      return <span className="w-fit justify-self-start rounded-full border border-primary-tint-border bg-primary-tint px-2 py-0.5 text-xs text-primary-700">Org-wide</span>;
    }
    if (a && 'workspaceIds' in a) {
      return <span className="w-fit justify-self-start rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-xs text-blue-800 dark:border-blue-800 dark:bg-blue-900/30 dark:text-blue-300">{a.workspaceIds.length} workspaces</span>;
    }
    return <span className="w-fit justify-self-start rounded-full border border-border-light dark:border-border-dark-hairlinebg-neutral-100 dark:bg-background-dark-tertiary px-2 py-0.5 text-xs text-text-tertiary dark:text-text-dark-tertiary">Private</span>;
  };

  const iconBtn = (label: string, Icon: React.ComponentType<{ className?: string }>, onClick: () => void, danger = false) => (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={onClick}
      className={`rounded p-1.5 text-primary-700 dark:text-primary-dark-400 ${danger ? 'hover:bg-red-50 hover:text-red-700 dark:hover:bg-red-900/30 dark:hover:text-red-400' : 'hover:bg-primary-100 dark:hover:bg-primary-900/40'}`}
    >
      <Icon className="w-4 h-4" />
    </button>
  );

  if (!enabled) return null;

  return (
    <PageShell
      title="Reports"
      description={`Reports Capitol Commons maintains, plus everything ${organization?.name || 'your organization'} has built.`}
      helpContentKey="ngr-reports"
      actions={
        <div className="flex items-center gap-3">
          <SearchField
            value={search}
            onChange={(v) => {
              setSearch(v);
              setPage(1);
            }}
            placeholder="Search reports"
            ariaLabel="Search reports"
            className="w-[236px]"
          />
          {isOrgAdmin && (
            <Button variant="primary" icon={AddIcon} onClick={() => setWizard({ open: true, editing: null })}>
              New report
            </Button>
          )}
        </div>
      }
    >
      <div className="space-y-[26px] p-6">
        {/* ---- 1. Standard reports shelf ---- */}
        <section aria-labelledby="standard-reports-heading">
          <SectionHeading
            id="standard-reports-heading"
            className="mb-3"
            note={`${STANDARD_REPORTS.length} maintained by Capitol Commons`}
            right={
              <span className="flex items-center gap-1.5">
                <LockIcon className="w-3.5 h-3.5" />
                Run, share a link, or embed — the definitions can&apos;t be changed.
              </span>
            }
          >
            Standard reports
          </SectionHeading>

          <div className="grid grid-cols-1 gap-[14px] md:grid-cols-2 xl:grid-cols-3">
            {STANDARD_REPORTS.map((sr) => (
              <div
                key={sr.key}
                className="card rounded-2xl border border-border-light dark:border-border-dark-hairlinebg-white dark:bg-background-dark-secondary p-4 transition hover:-translate-y-[3px] hover:border-primary-200 hover:shadow-lg"
              >
                <div className="flex items-start gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-[10px] border border-primary-100 bg-primary-50 dark:border-border-dark-hairline dark:bg-primary-900/20">
                    <sr.icon className="w-4 h-4 text-primary-600 dark:text-primary-dark-400" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-[14.5px] text-text-primary dark:text-text-dark-primary">{sr.name}</div>
                    <div className="text-[11.5px] text-text-tertiary dark:text-text-dark-tertiary">{sr.cadence}</div>
                  </div>
                  <span className="flex items-center gap-1 rounded-full border border-primary-tint-border bg-primary-tint px-2 py-0.5 text-[9.5px] font-bold uppercase tracking-[.06em] text-primary-700">
                    <LockIcon className="w-2.5 h-2.5" /> Standard
                  </span>
                </div>
                <p className="mt-2 line-clamp-2 text-[12.5px] leading-6 text-text-secondary dark:text-text-dark-secondary [text-wrap:pretty]">
                  {sr.description}
                </p>
                <div className="mt-3 flex items-center justify-between border-t border-border-light dark:border-border-dark-hairlinept-3">
                  <span className="rounded bg-neutral-100 dark:bg-background-dark-tertiary px-2 py-0.5 text-[10.5px] uppercase text-text-secondary dark:text-text-dark-secondary">
                    {sr.dataset}
                  </span>
                  <div className="flex items-center gap-1.5">
                    <Button
                      variant="outline"
                      size="sm"
                      icon={RunIcon}
                      onClick={() =>
                        presetByKey[sr.key]
                          ? setRunPreset(presetByKey[sr.key])
                          : navigate(`/reports-legacy?report=${sr.key}`)
                      }
                    >
                      Run
                    </Button>
                    <Button variant="ghost" size="sm" icon={ShareIcon} onClick={shareStub}>
                      Share link
                    </Button>
                    {iconBtn('Embed code', EmbedIcon, embedStub)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ---- 2. Your reports ---- */}
        <section aria-labelledby="your-reports-heading">
          <SectionHeading
            id="your-reports-heading"
            className="mb-3"
            note={`${(reports || []).length} built by your organization`}
          >
            Your reports
          </SectionHeading>

          {!isLoading && (reports || []).length === 0 ? (
            <EmptyState
              size="section"
              icon={FileBarChart}
              title="No reports yet"
              hint={
                <>
                  Nothing has been built for {organization?.name || 'your organization'} yet. Build one in
                  five steps — dataset, fields, workspaces, preview, save. The{' '}
                  {STANDARD_REPORTS.length} standard reports above already run, with no setup.
                  <span className="mt-2 block text-[11.5px] text-text-tertiary dark:text-text-dark-tertiary">
                    Only organization admins can author reports. Everyone else sees what&apos;s been made
                    available to their workspaces.
                  </span>
                </>
              }
              action={
                <div className="flex items-center gap-2">
                  {isOrgAdmin && (
                    <Button variant="primary" icon={AddIcon} onClick={() => setWizard({ open: true, editing: null })}>
                      New report
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    onClick={() => document.getElementById('standard-reports-heading')?.scrollIntoView({ behavior: 'smooth' })}
                  >
                    Browse standard reports
                  </Button>
                </div>
              }
            />
          ) : (
            <>
              <StandardTable loading={isLoading}>
                <StandardTableHeader>
                  <StandardTableHeaderCell>Report</StandardTableHeaderCell>
                  <StandardTableHeaderCell>Workspace scope</StandardTableHeaderCell>
                  <StandardTableHeaderCell>Availability</StandardTableHeaderCell>
                  <StandardTableHeaderCell>Last run</StandardTableHeaderCell>
                  <StandardTableHeaderCell>Created</StandardTableHeaderCell>
                  <StandardTableHeaderCell align="right" columnType="actions">
                    <span className="inline-flex items-center gap-1.5">
                      Actions
                      <ActionsHeaderHelp
                        icons={[
                          'run',
                          'share',
                          'embed',
                          'preview',
                          { key: 'edit', note: 'Org admins only.' },
                          { key: 'duplicate', note: 'Org admins only.' },
                          'download',
                          { key: 'delete', note: 'Org admins only.' },
                        ]}
                      />
                    </span>
                  </StandardTableHeaderCell>
                </StandardTableHeader>
                <StandardTableBody>
                  {paged.length === 0 ? (
                    <EmptyTableState colSpan={6} message="Nothing matches that search." />
                  ) : (
                    paged.map((r) => (
                      <StandardTableRow key={r.id}>
                        <StandardTableCell>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-[13.5px] text-text-primary dark:text-text-dark-primary">{r.name}</span>
                            <span className="rounded bg-neutral-100 dark:bg-background-dark-tertiary px-1.5 py-0.5 text-[10.5px] uppercase text-text-secondary dark:text-text-dark-secondary">
                              {(r.config?.query?.measures?.[0] || r.config?.query?.dimensions?.[0] || '').split('.')[0] || 'custom'}
                            </span>
                          </div>
                          {r.description && (
                            <div className="max-w-[360px] truncate text-[11.5px] text-text-tertiary dark:text-text-dark-tertiary">{r.description}</div>
                          )}
                        </StandardTableCell>
                        <StandardTableCell>
                          {r.config?.scope?.workspaceIds === 'all'
                            ? 'All workspaces'
                            : `${(r.config?.scope?.workspaceIds || []).length} selected`}
                        </StandardTableCell>
                        <StandardTableCell>{availabilityPill(r)}</StandardTableCell>
                        <StandardTableCell>
                          {r.last_run_at ? (
                            <div className="tabular-nums">
                              <div>{formatDateShort(r.last_run_at)}</div>
                              <div className="text-[11px] text-text-tertiary dark:text-text-dark-tertiary">{formatTime(r.last_run_at)}</div>
                            </div>
                          ) : (
                            <span className="text-text-tertiary dark:text-text-dark-tertiary">{EMPTY_VALUE}</span>
                          )}
                        </StandardTableCell>
                        <StandardTableCell>
                          <div>{formatDate(r.created_at)}</div>
                          {r.owner_name && <div className="text-[11px] text-text-tertiary dark:text-text-dark-tertiary">{r.owner_name}</div>}
                        </StandardTableCell>
                        <StandardTableCell align="right">
                          <div className="flex items-center justify-end gap-0.5">
                            {iconBtn('Run', RunIcon, () => setRunReport(r))}
                            {iconBtn('Share', ShareIcon, shareStub)}
                            {iconBtn('Embed code', EmbedIcon, embedStub)}
                            {iconBtn('Preview', PreviewIcon, () => setRunReport(r))}
                            {isOrgAdmin && iconBtn('Edit', EditIcon, () => setWizard({ open: true, editing: r }))}
                            {isOrgAdmin && iconBtn('Duplicate', DuplicateIcon, () => duplicate.mutate(r))}
                            {iconBtn('Export', DownloadIcon, () => exportCsv(r))}
                            {isOrgAdmin &&
                              iconBtn('Delete', DeleteIcon, () => {
                                if (window.confirm(`Delete "${r.name}"? This can't be undone.`)) remove.mutate(r);
                              }, true)}
                          </div>
                        </StandardTableCell>
                      </StandardTableRow>
                    ))
                  )}
                </StandardTableBody>
              </StandardTable>

              <div className="flex items-center justify-between border-t border-border-light dark:border-border-dark-hairlinebg-neutral-50 dark:bg-background-dark-tertiary px-4 py-2 text-xs text-text-secondary dark:text-text-dark-secondary">
                <span>
                  {search.trim()
                    ? `Showing ${filtered.length} of ${(reports || []).length} reports`
                    : `${(reports || []).length} reports · ${availableCount} available to workspaces`}
                </span>
                <button
                  type="button"
                  className="text-primary-700 dark:text-primary-dark-400 hover:underline"
                  title="Availability controls arrive with the sharing update"
                  onClick={() =>
                    setStub({
                      title: 'Manage availability',
                      body: 'Choosing which workspaces see each report arrives with the availability update. Reports stay private to their author until then.',
                    })
                  }
                >
                  Manage availability
                </button>
              </div>
              {pageCount > 1 && (
                <Pagination page={page} pageCount={pageCount} onPageChange={setPage} total={filtered.length} pageSize={PAGE_SIZE} noun="reports" />
              )}
            </>
          )}
        </section>
      </div>

      {/* Stub dialog for share/embed/availability affordances */}
      {stub && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" role="dialog" aria-modal="true">
          <div className="w-full max-w-md rounded-lg bg-white dark:bg-background-dark-secondary p-6 shadow-xl">
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-base font-semibold text-text-primary dark:text-text-dark-primary">{stub.title}</h3>
              {iconBtn('Close', CloseIcon, () => setStub(null))}
            </div>
            <p className="text-sm text-text-secondary dark:text-text-dark-secondary">{stub.body}</p>
            <div className="mt-4 flex justify-end">
              <Button variant="outline" onClick={() => setStub(null)}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Run/Preview results modal */}
      {runPreset && organizationId && (
        <NgrPresetRunModal
          organizationId={organizationId}
          preset={runPreset}
          onClose={() => setRunPreset(null)}
        />
      )}

      {runReport && organizationId && (
        <NgrRunModal
          organizationId={organizationId}
          report={runReport}
          onClose={() => setRunReport(null)}
        />
      )}

      {wizard.open && organizationId && (
        <NgrReportWizard
          organizationId={organizationId}
          workspaces={(workspaces || []).map((w) => ({ id: w.id, name: w.name }))}
          editing={wizard.editing}
          onClose={() => setWizard({ open: false, editing: null })}
          onSaved={() => setWizard({ open: false, editing: null })}
        />
      )}
    </PageShell>
  );
};

/** Executes a saved definition and renders the rows on the report grid theme. */
const NgrRunModal: React.FC<{
  organizationId: string;
  report: NgrReportDefinition;
  onClose: () => void;
}> = ({ organizationId, report, onClose }) => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['ngr-run', organizationId, report.id],
    queryFn: () => executeNgrQuery(organizationId, report.config.query),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" role="dialog" aria-modal="true">
      <div className="flex h-[70vh] w-full max-w-4xl flex-col rounded-lg bg-white dark:bg-background-dark-secondary p-6 shadow-xl">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-base font-semibold text-text-primary dark:text-text-dark-primary">{report.name}</h3>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
        </div>
        <div className="min-h-0 flex-1">
          {isLoading && <div className="p-6 text-sm text-text-secondary dark:text-text-dark-secondary">Running…</div>}
          {isError && <div className="p-6 text-sm text-red-600 dark:text-red-400">Run failed — the reporting engine may be unavailable.</div>}
          {data && <NgrResultsGrid rows={data.rows} />}
        </div>
      </div>
    </div>
  );
};

/**
 * Runs a Cube-backed standard report. Columns come from the preset rather than
 * being derived from the result keys, so headers read as the report's own
 * labels — including the organization's custom field names (PRD §5.7).
 */
const NgrPresetRunModal: React.FC<{
  organizationId: string;
  preset: NgrPreset;
  onClose: () => void;
}> = ({ organizationId, preset, onClose }) => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['ngr-preset-run', organizationId, preset.key],
    queryFn: () => executeNgrPreset(organizationId, preset.key),
  });

  const columnDefs = useMemo(
    () => (data ? presetColumnDefs(data.columns, data.rows) : []),
    [data]
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" role="dialog" aria-modal="true">
      <div className="flex h-[70vh] w-full max-w-5xl flex-col rounded-lg bg-white dark:bg-background-dark-secondary p-6 shadow-xl">
        <div className="mb-3 flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-text-primary dark:text-text-dark-primary">{preset.name}</h3>
            <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
              {data ? `${data.rows.length} rows · ${data.executionTimeMs}ms` : 'Standard report'}
            </p>
          </div>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
        </div>
        <div className="min-h-0 flex-1">
          {isLoading && <div className="p-6 text-sm text-text-secondary dark:text-text-dark-secondary">Running…</div>}
          {isError && (
            <div className="p-6 text-sm text-red-600 dark:text-red-400">
              Run failed — the reporting engine may be unavailable.
            </div>
          )}
          {data && (
            <AgGridReact
              theme={reportGridLightTheme}
              rowData={data.rows}
              columnDefs={columnDefs}
              defaultColDef={{ sortable: true, resizable: true, flex: 1, minWidth: 120 }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

const NgrResultsGrid: React.FC<{ rows: Record<string, unknown>[] }> = ({ rows }) => {
  const columnDefs = cubeColumnDefs(rows);
  return (
    <AgGridReact
      theme={reportGridLightTheme}
      rowData={rows}
      columnDefs={columnDefs}
      defaultColDef={{ sortable: true, resizable: true, flex: 1, minWidth: 120 }}
    />
  );
};

export default NgrReportsPage;
